import React, { useState, useEffect } from "react";
import { Form } from "antd";
import { useMercuryContext } from "../../context";
import { successfulNotification, failedNotification } from "../../common/utils/notifications";
import TeamForm from "./TeamForm";
import { Team, TeamRequestProps } from "./TeamInterfaces";

const TeamRequest: React.FC<TeamRequestProps> = ({
  initialValues,
  buttonLabel,
  refresh,
  applicationsList,
  hasPermissions,
  membersList,
  disabled
}) => {
  const [form] = Form.useForm();
  const { umsClient } = useMercuryContext();
  const [isOpen, setIsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentTeamId, setCurrentTeamId] = useState<string | null>(null);

  const openDrawer = () => {
    setIsOpen(true);
    form.resetFields();
    if (initialValues) {
      form.setFieldsValue(initialValues);
      setCurrentTeamId(initialValues._id ?? null);
    }
  };

  const closeDrawer = () => {
    setIsOpen(false);
    form?.resetFields();
    setCurrentTeamId(null);
  };

  useEffect(() => {
    if (initialValues) {
      setCurrentTeamId(initialValues?._id ?? null);
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const submitTeam = async (values: Team) => {
    setSubmitting(true);
    try {
      const payload: any = {
        name: values.name,
        description: values.description,
        customerApplication: values.customerApplication,
        members: values.members || [],
      };

      if (currentTeamId) {
        payload.isActive = values.isActive ?? true;
        await umsClient.patch(`/ums-v1/teams/${currentTeamId}`, payload);
      } else {
        await umsClient.post("/ums-v1/teams", payload);
      }

      closeDrawer();
      successfulNotification(
        `Team has been successfully ${currentTeamId ? "updated" : "added"}`,
        "Please refresh your page if the update is not visible."
      );
      refresh();
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.details?.length
          ? error.response.data.details
            .flatMap((d: any) => d.errors || [])
            .join("; ")
          : error?.response?.data?.message || error?.message || error?.toString();

      failedNotification(`Failed to ${currentTeamId ? "update" : "add"} team`, errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TeamForm
      form={form}
      buttonLabel={buttonLabel}
      isOpen={isOpen}
      openDrawer={openDrawer}
      closeDrawer={closeDrawer}
      onSubmit={submitTeam}
      submitting={submitting}
      applicationsList={applicationsList}
      membersList={membersList}
      initialValues={initialValues}
      hasPermissions={hasPermissions}
    />
  );
};

export default TeamRequest;
